// PHASE_4 / B1-frontend — task snooze modal (assignee_self snooze affordance).
//
// Wires POST /v1/tasks/:id/snooze { until } — the assignee hides the task until
// a future time, after which it returns to the inbox. The route REJECTS a
// non-future `until` (validation) and 409s a terminal task; both surface here as
// honest envelope errors (toUiError → t(messageKey)). The picker is a native
// datetime-local input (the raw-input + var()-token pattern the admin inventory
// filter bar already uses) — no money, no float, no raw hex/ms.

import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "@novemai/components";

import { errorMessage } from "@/api/errors";
import { snoozeTask, type TaskRow } from "./api";

/** Default the picker one hour ahead, formatted for a datetime-local input. */
function defaultSnoozeLocal(): string {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  // datetime-local wants `YYYY-MM-DDTHH:mm` in LOCAL time; trim the seconds/zone.
  const pad = (n: number): string => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

export interface SnoozeModalProps {
  readonly task: TaskRow;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSnoozed: () => void;
}

export function SnoozeModal({ task, open, onClose, onSnoozed }: SnoozeModalProps): React.ReactElement {
  const { t } = useTranslation();
  const [until, setUntil] = useState(defaultSnoozeLocal);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(): Promise<void> {
    setSubmitError(null);
    const when = new Date(until);
    // Client-side guard mirrors the server's future-only rule (honest UX); the
    // route is still the authority and re-validates.
    if (Number.isNaN(when.getTime()) || when.getTime() <= Date.now()) {
      setSubmitError(t("tasks.snooze_error_future"));
      return;
    }
    setSubmitting(true);
    try {
      await snoozeTask(task.task_id, { until: when.toISOString() });
      onSnoozed();
      onClose();
    } catch (err) {
      setSubmitError(errorMessage(err, t));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose}>
      <ModalHeader title={t("tasks.snooze_title")} />
      <ModalBody>
        <p className="mb-3 text-sm text-[var(--color-muted-foreground)]">
          {t("tasks.snooze_subtitle")}
        </p>
        {submitError !== null && (
          <p role="alert" className="mb-3 text-sm text-[var(--color-error)]">
            {submitError}
          </p>
        )}
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-[var(--color-muted-foreground)]">
            {t("tasks.snooze_until_label")}
          </span>
          <input
            type="datetime-local"
            aria-label={t("tasks.snooze_until_label")}
            className="rounded border px-3 py-2 text-sm"
            value={until}
            onChange={(e): void => setUntil(e.target.value)}
          />
        </label>
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" size="sm" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <Button variant="primary" size="sm" onClick={(): void => void onSubmit()} disabled={submitting}>
          {t("tasks.snooze_submit")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
